import { useCallback, useState, useEffect } from 'react';
import groupBy from 'lodash/groupBy';
import { isGroupItem } from '@atlassian/jira-business-list-handlers';
import { useGroupBy } from '../../group-by';
import { useHierarchyStore } from '../../hierarchy';
import { useBulkSelectionStore } from '../bulk-selection-store';
import { useSelectCollapsedGroups } from '../select-collapsed-groups';
import { getAllIssueChildren, getFlattenIssuesList } from '../utils';

export const useShiftKeySelection = () => {
	const [groupByFieldId] = useGroupBy();
	const [{ issuesMap, expandedItems, hierarchyData, childrenItems }] = useHierarchyStore();
	const [
		{ selectedIssues: selectedIssuesStore, fetchingGroups, lastSelectedRowIndex },
		{ selectIssues, unselectIssues, setLastSelectedRowIndex },
	] = useBulkSelectionStore();
	const { selectCollapsedGroups } = useSelectCollapsedGroups();

	/*
        If grouping, the last lastSelectedRowIndex could change  due to expanding and fetching a group
        Once we finish with the fetching of the groups we should update lastSelectedRowIndex with the new index
    */

	const [lastIssueIdSelected, setLastIssueIdSelected] = useState<string>();
	useEffect(() => {
		const isGrouping = groupByFieldId != null;
		if (
			isGrouping &&
			!fetchingGroups.length &&
			lastSelectedRowIndex !== undefined &&
			lastIssueIdSelected
		) {
			const issues = getFlattenIssuesList({ issues: hierarchyData.data, expandedItems });
			const newIndex = issues.findIndex((i) => String(i.issueId) === lastIssueIdSelected);
			if (newIndex >= 0) {
				setLastSelectedRowIndex(newIndex);
				setLastIssueIdSelected(undefined);
			}
		}
		// Should only update the last selected row index once
		// the expanded items are updated and the groups are finally fetched
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expandedItems, fetchingGroups]);

	const onShiftKeyBulkSelection = useCallback(
		({ isChecked, rowIndex: selectedRowIndex }: { isChecked: boolean; rowIndex?: number }) => {
			let rowIndex = selectedRowIndex;
			if (lastSelectedRowIndex !== undefined && rowIndex !== undefined) {
				const lastIssueSelected = issuesMap.get([...selectedIssuesStore].pop() || '');
				const parentLastIssueSelected = lastIssueSelected?.parentIssueId;
				let expandedItemsToGetFlattenList = expandedItems;

				if (
					parentLastIssueSelected &&
					!isGroupItem(parentLastIssueSelected) &&
					!expandedItems[parentLastIssueSelected]
				) {
					/*
                    Edge case
                        If the last issue selected it's a child of a collapsed task/epic
                        We should simulate the parent as expanded because we need to select the hidden children
                        */
					expandedItemsToGetFlattenList = {
						...expandedItemsToGetFlattenList,
						[parentLastIssueSelected]: true,
					};
					rowIndex += issuesMap.get(parentLastIssueSelected)?.children?.length || 0;
				}

				const issues = expandedItems
					? getFlattenIssuesList({
							issues: hierarchyData.data,
							expandedItems: expandedItemsToGetFlattenList,
						})
					: hierarchyData.data;

				let selectedIssues = [];
				if (lastSelectedRowIndex < rowIndex) {
					selectedIssues = issues.slice(lastSelectedRowIndex, rowIndex + 1);
				} else {
					selectedIssues = issues.slice(
						rowIndex,
						!isChecked ? lastSelectedRowIndex + 1 : lastSelectedRowIndex,
					);
				}

				let issueIds: string[] = [];

				const isGrouping = groupByFieldId != null;
				if (isGrouping) {
					const groups = groupBy(selectedIssues, (issue) => isGroupItem(issue.issueId));
					// select/unselect issues that are in the middle and are not groups
					issueIds = groups.false.map((issue) => String(issue.issueId));

					// Should select and expand the groups in the middle but not the parent of the selected issue
					const pressedIssue = issues[rowIndex];
					const groupsToSelect =
						groups.true?.filter((group) => group.issueId !== pressedIssue.parentIssueId) || [];

					if (groupsToSelect.length) {
						/*
                            if there are groups in the middle of the selection we should keep
                            the last issue selected to update the row index because could change
                            depending on the groups that are going to be expanded
                        */
						setLastIssueIdSelected(pressedIssue.issueId);
						if (isChecked) {
							selectCollapsedGroups(groupsToSelect);
						} else {
							groupsToSelect.forEach((group) => {
								const groupIssues = getAllIssueChildren({
									issue: group,
									childrenItems,
								});
								unselectIssues({
									issues: groupIssues || [],
									unselectGroup: group.issueId,
									rowIndex,
								});
							});
						}
					}
				} else {
					issueIds = selectedIssues.map((issue) => String(issue.issueId));
				}

				if (!isChecked) {
					unselectIssues({ issues: issueIds });
				} else {
					selectIssues({ issues: issueIds });
				}
			}
		},
		[
			childrenItems,
			expandedItems,
			groupByFieldId,
			hierarchyData.data,
			issuesMap,
			lastSelectedRowIndex,
			selectCollapsedGroups,
			selectIssues,
			selectedIssuesStore,
			unselectIssues,
		],
	);

	return { onShiftKeyBulkSelection };
};
